/* Inquiry content packs — one per solution area.
 *
 * The header CTA used to open a single modal whose first step asked which of
 * four mixed areas you wanted. The tab already answers that, so each area now
 * carries its own pack and the modal opens straight on the real question.
 *
 * A pack is data, not markup. InquiryModal walks `steps[].fields[]` and renders
 * each field by `kind`:
 *   multi / single  — pill rows over `options`
 *   text / email / tel / textarea — floating-label inputs
 * Options carry an id plus both languages, so stored answers stay
 * language-independent and the filter maps below key on ids, not on copy.
 *
 *   optionsBy — options depend on another field's answer (concierge: industry)
 *   filterBy  — options are narrowed by another field's answers (painpoints)
 */

const TIMELINE = [
  { id: 'asap', de: 'Sofort', en: 'ASAP' },
  { id: 'month', de: '30 Tage', en: '30 days' },
  { id: 'quarter', de: 'Dieses Quartal', en: 'This quarter' },
  { id: 'open', de: 'Offen', en: 'Open' },
];

const BUDGET = [
  { id: '5k', de: 'bis 5k €', en: 'up to €5k' },
  { id: '15k', de: '5–15k €', en: '€5–15k' },
  { id: '30k', de: '15–30k €', en: '€15–30k' },
  { id: '30kplus', de: '30k+ €', en: '€30k+' },
];

const CONTACT_FIELDS = [
  { id: 'name', kind: 'text', icon: 'user', required: true, de: 'Name', en: 'Name', placeholder: { de: 'Vor- und Nachname', en: 'Full name' } },
  { id: 'email', kind: 'email', icon: 'mail', required: true, de: 'Geschäftliche E-Mail', en: 'Work email', placeholder: { de: 'name@unternehmen.de', en: 'name@company.com' } },
  { id: 'company', kind: 'text', icon: 'building', required: true, de: 'Unternehmen', en: 'Company', placeholder: { de: 'Firmenname', en: 'Company name' } },
  { id: 'website', kind: 'text', icon: 'globe', required: true, de: 'Website', en: 'Website', placeholder: { de: 'https://…', en: 'https://…' } },
];

const DETAILS_FIELD = {
  id: 'details',
  kind: 'textarea',
  icon: 'message',
  de: 'Zusätzliche Infos (optional)',
  en: 'Anything else (optional)',
  placeholder: { de: 'Ziele, Anforderungen oder offene Fragen.', en: 'Goals, requirements or open questions.' },
};

/* ------------------------------------------------------------------ DESIGN */

const design = {
  type: 'design_inquiry',
  subject: { de: 'Design-Anfrage', en: 'Design Inquiry' },
  eyebrow: { de: 'Design', en: 'Design' },
  title: { de: 'Strategiegespräch buchen', en: 'Book an orientation call' },
  subtitle: {
    de: 'Vier kurze Schritte. Danach weiß CRE8NOW genug, um im Gespräch konkret zu werden.',
    en: 'Four short steps. Enough for CRE8NOW to get concrete on the call.',
  },
  steps: [
    {
      label: { de: 'Projekt', en: 'Project' },
      fields: [{
        id: 'projectType',
        role: 'goals',
        kind: 'multi',
        icon: 'zap',
        required: true,
        de: 'Worum geht es?',
        en: 'What are we building?',
        tooltip: {
          de: 'Mehrfachauswahl. Das steckt ab, wer aus dem Team beim Gespräch dabei ist.',
          en: 'Pick as many as apply. It decides who from the team joins the call.',
        },
        options: [
          { id: 'web', de: 'Neue Website / Landingpage', en: 'New website / landing page' },
          { id: 'productui', de: 'Produkt- / App-UI', en: 'Product / app UI' },
          { id: 'redesign', de: 'Redesign bestehender Auftritt', en: 'Redesign of an existing site' },
          { id: 'designsystem', de: 'Designsystem', en: 'Design system' },
          { id: 'ux', de: 'UX-Konzept & Flows', en: 'UX concept & flows' },
          { id: 'build', de: 'Webflow- / Frontend-Umsetzung', en: 'Webflow / frontend build' },
          { id: 'brand', de: 'Brand & Visual Identity', en: 'Brand & visual identity' },
          { id: 'motion', de: 'Motion & Social Assets', en: 'Motion & social assets' },
        ],
      }],
    },
    {
      label: { de: 'Status', en: 'Status' },
      fields: [
        {
          id: 'painpoints',
          kind: 'multi',
          icon: 'alert',
          required: true,
          de: 'Was läuft heute nicht?',
          en: "What isn't working today?",
          tooltip: {
            de: 'Zeigt CRE8NOW, wo der größte Hebel liegt.',
            en: 'Shows CRE8NOW where the biggest leverage sits.',
          },
          options: [
            { id: 'convert', de: 'Konvertiert nicht', en: "Doesn't convert" },
            { id: 'dated', de: 'Sieht veraltet aus', en: 'Looks dated' },
            { id: 'mobile', de: 'Mobile bricht', en: 'Mobile breaks' },
            { id: 'inconsistent', de: 'Inkonsistent über Seiten', en: 'Inconsistent across pages' },
            { id: 'handoff', de: 'Handoff an Dev ist zäh', en: 'Dev handoff is slow' },
            { id: 'content', de: 'Content-Pflege blockiert', en: 'Content upkeep is blocked' },
            { id: 'concept', de: 'Kein klares Konzept', en: 'No clear concept' },
            { id: 'slow', de: 'Umsetzung zu langsam', en: 'Delivery too slow' },
          ],
          filterBy: {
            field: 'projectType',
            map: {
              web: ['convert', 'dated', 'mobile', 'content', 'slow'],
              productui: ['inconsistent', 'mobile', 'concept', 'handoff'],
              redesign: ['dated', 'convert', 'inconsistent', 'mobile'],
              designsystem: ['inconsistent', 'handoff', 'dated'],
              ux: ['concept', 'convert', 'mobile', 'inconsistent'],
              build: ['handoff', 'slow', 'content', 'mobile'],
              brand: ['dated', 'inconsistent', 'concept'],
              motion: ['dated', 'convert', 'slow'],
            },
          },
        },
        {
          id: 'assets', mailLabel: 'EXISTING MATERIAL',
          kind: 'multi',
          icon: 'database',
          de: 'Was liegt schon vor? (optional)',
          en: 'What already exists? (optional)',
          tooltip: {
            de: 'Bestehendes Material verkürzt die Startphase spürbar.',
            en: 'Existing material shortens the ramp-up noticeably.',
          },
          options: [
            { id: 'figma', de: 'Figma-Files', en: 'Figma files' },
            { id: 'brandguide', de: 'Brand Guide', en: 'Brand guide' },
            { id: 'website', de: 'Bestehende Website', en: 'Existing website' },
            { id: 'nothing', de: 'Nichts davon', en: 'None of it' },
          ],
        },
      ],
    },
    {
      label: { de: 'Rahmen', en: 'Scope' },
      fields: [
        {
          id: 'timeline', kind: 'single', icon: 'calendar', required: true,
          de: 'Gewünschter Start', en: 'Preferred start',
          tooltip: { de: 'Hilft CRE8NOW bei der Ressourcenplanung.', en: 'Helps CRE8NOW plan resources.' },
          options: TIMELINE,
        },
        {
          id: 'budget', kind: 'single', icon: 'chart', required: true,
          de: 'Budgetrahmen', en: 'Budget range',
          tooltip: { de: 'Bestimmt Strategie und Umsetzungstiefe.', en: 'Decides strategy and depth of execution.' },
          options: BUDGET,
        },
        {
          id: 'addons', mailLabel: 'ADD-ONS', kind: 'multi', icon: 'sparkles',
          de: 'Zusätzlich gewünscht? (optional)', en: 'Anything on top? (optional)',
          tooltip: {
            de: 'Audits und Trainings laufen begleitend zum Projekt.',
            en: 'Audits and trainings run alongside the project.',
          },
          options: [
            { id: 'uxaudit', de: 'UX/UI-Audit', en: 'UX/UI audit' },
            { id: 'designaudit', de: 'Design-Audit', en: 'Design audit' },
            { id: 'perfaudit', de: 'Performance-Audit', en: 'Performance audit' },
            { id: 'figmatraining', de: 'Figma-Training', en: 'Figma training' },
            { id: 'webflowtraining', de: 'Webflow-Training', en: 'Webflow training' },
            { id: 'designthinking', de: 'Design-Thinking-Workshop', en: 'Design thinking workshop' },
          ],
        },
      ],
    },
    {
      label: { de: 'Kontakt', en: 'Contact' },
      fields: [...CONTACT_FIELDS, DETAILS_FIELD],
    },
  ],
  submit: { de: 'Anfrage senden', en: 'Send inquiry' },
  confirmation: {
    title: { de: 'Anfrage angekommen', en: 'Inquiry received' },
    text: {
      de: 'CRE8NOW sieht sich deinen aktuellen Auftritt vor dem Gespräch an und meldet sich innerhalb eines Werktags mit einem Terminvorschlag.',
      en: 'CRE8NOW reviews your current site before the call and gets back within one business day with a proposed time.',
    },
  },
};

/* ---------------------------------------------------------------------- AI */

const ai = {
  type: 'ai_inquiry',
  subject: { de: 'AI-Anfrage', en: 'AI Inquiry' },
  eyebrow: { de: 'AI Solutions', en: 'AI Solutions' },
  title: { de: 'KI-Strategiegespräch buchen', en: 'Book an AI orientation call' },
  subtitle: {
    de: 'Vier kurze Schritte. Danach kann CRE8NOW einschätzen, was sich bei euch zuerst rechnet.',
    en: 'Four short steps. Enough for CRE8NOW to judge what pays off first.',
  },
  steps: [
    {
      label: { de: 'Einsatz', en: 'Use case' },
      fields: [{
        id: 'useCase', role: 'goals', kind: 'multi', icon: 'zap', required: true,
        de: 'Wo soll KI arbeiten?', en: 'Where should AI do the work?',
        tooltip: {
          de: 'Mehrfachauswahl. Unklar ist eine gültige Antwort — dafür gibt es die letzte Option.',
          en: "Pick as many as apply. Unsure is a valid answer — that's the last option.",
        },
        options: [
          { id: 'support', de: 'Kundenanfragen & Support', en: 'Customer inquiries & support' },
          { id: 'booking', de: 'Terminbuchung', en: 'Appointment booking' },
          { id: 'leadqual', de: 'Lead-Qualifizierung', en: 'Lead qualification' },
          { id: 'automation', de: 'Interne Prozesse / Automation', en: 'Internal processes / automation' },
          { id: 'knowledge', de: 'Dokumente & Wissen (RAG)', en: 'Documents & knowledge (RAG)' },
          { id: 'content', de: 'Content-Produktion', en: 'Content production' },
          { id: 'voice', de: 'Voice / Telefonie', en: 'Voice / phone' },
          { id: 'unsure', de: 'Noch offen — beratet mich', en: 'Not sure yet — advise me' },
        ],
      }],
    },
    {
      label: { de: 'Engpass', en: 'Bottleneck' },
      fields: [
        {
          id: 'painpoints', kind: 'multi', icon: 'alert', required: true,
          de: 'Was kostet euch heute Zeit?', en: "What's eating your time?",
          tooltip: {
            de: 'Automatisierung lohnt sich dort, wo sich etwas täglich wiederholt.',
            en: 'Automation pays off where something repeats daily.',
          },
          options: [
            { id: 'supportload', de: 'Team hängt im Dauer-Support', en: 'Team stuck in support' },
            { id: 'unreachable', de: 'Telefon oft nicht erreichbar', en: 'Phone goes unanswered' },
            { id: 'leadsidle', de: 'Leads bleiben liegen', en: 'Leads sit untouched' },
            { id: 'samequestions', de: 'Immer die gleichen Fragen', en: 'Same questions every day' },
            { id: 'manualdata', de: 'Manuelle Datenübertragung', en: 'Manual data entry' },
            { id: 'slowreplies', de: 'Antwortzeiten zu lang', en: 'Response times too long' },
            { id: 'noknowhow', de: 'Kein KI-Know-how im Team', en: 'No AI know-how in-house' },
            { id: 'privacy', de: 'Unsicherheit beim Datenschutz', en: 'Unclear on data protection' },
          ],
          filterBy: {
            field: 'useCase',
            map: {
              support: ['supportload', 'samequestions', 'slowreplies'],
              booking: ['unreachable', 'leadsidle', 'samequestions'],
              leadqual: ['leadsidle', 'slowreplies', 'manualdata'],
              automation: ['manualdata', 'supportload', 'slowreplies'],
              knowledge: ['samequestions', 'noknowhow', 'manualdata'],
              content: ['supportload', 'noknowhow'],
              voice: ['unreachable', 'supportload', 'leadsidle'],
              unsure: ['noknowhow', 'privacy', 'supportload'],
            },
          },
        },
        {
          id: 'systems', mailLabel: 'SYSTEMS IN USE', kind: 'multi', icon: 'database',
          de: 'Welche Systeme sind im Einsatz? (optional)', en: 'Which systems are in use? (optional)',
          tooltip: {
            de: 'Bestimmt, woran die Automatisierung andocken kann.',
            en: 'Decides what the automation can dock onto.',
          },
          options: [
            { id: 'crm', de: 'CRM', en: 'CRM' },
            { id: 'calendar', de: 'Kalender', en: 'Calendar' },
            { id: 'phone', de: 'Telefonanlage', en: 'Phone system' },
            { id: 'shop', de: 'Shop', en: 'Shop' },
            { id: 'erp', de: 'ERP / Warenwirtschaft', en: 'ERP / inventory' },
            { id: 'none', de: 'Nichts davon', en: 'None of it' },
          ],
        },
      ],
    },
    {
      label: { de: 'Rahmen', en: 'Scope' },
      fields: [
        {
          id: 'volume', mailLabel: 'INQUIRIES PER MONTH', kind: 'single', icon: 'chart',
          de: 'Anfragen pro Monat (optional)', en: 'Inquiries per month (optional)',
          tooltip: {
            de: 'Das Volumen entscheidet, ab wann sich eine Automatisierung trägt.',
            en: 'Volume decides when an automation starts to carry itself.',
          },
          options: [
            { id: 'lt100', de: 'unter 100', en: 'under 100' },
            { id: '100-500', de: '100–500', en: '100–500' },
            { id: '500-2000', de: '500–2.000', en: '500–2,000' },
            { id: 'gt2000', de: '2.000+', en: '2,000+' },
            { id: 'unknown', de: 'weiß ich nicht', en: "don't know" },
          ],
        },
        {
          id: 'timeline', kind: 'single', icon: 'calendar', required: true,
          de: 'Gewünschter Start', en: 'Preferred start',
          tooltip: { de: 'Hilft CRE8NOW bei der Ressourcenplanung.', en: 'Helps CRE8NOW plan resources.' },
          options: TIMELINE,
        },
        {
          id: 'budget', kind: 'single', icon: 'chart', required: true,
          de: 'Budgetrahmen', en: 'Budget range',
          tooltip: { de: 'Bestimmt Strategie und Umsetzungstiefe.', en: 'Decides strategy and depth of execution.' },
          options: [...BUDGET, { id: 'openbudget', de: 'noch offen', en: 'still open' }],
        },
        {
          id: 'addons', mailLabel: 'ADD-ONS', kind: 'multi', icon: 'sparkles',
          de: 'Zusätzlich gewünscht? (optional)', en: 'Anything on top? (optional)',
          tooltip: {
            de: 'Roadmaps und Trainings laufen begleitend zur Umsetzung.',
            en: 'Roadmaps and trainings run alongside the build.',
          },
          options: [
            { id: 'roadmap', de: 'KI-Roadmap / Strategie-Audit', en: 'AI roadmap / strategy audit' },
            { id: 'processanalysis', de: 'Prozess-Analyse', en: 'Process analysis' },
            { id: 'infraaudit', de: 'Infrastruktur-Audit', en: 'Infrastructure audit' },
            { id: 'teamonboarding', de: 'Team-Onboarding', en: 'Team onboarding' },
            { id: 'prompt', de: 'Prompt-Engineering-Workshop', en: 'Prompt engineering workshop' },
            { id: 'aiworkflow', de: 'AI-Workflow-Setup', en: 'AI workflow setup' },
          ],
        },
      ],
    },
    {
      label: { de: 'Kontakt', en: 'Contact' },
      fields: [...CONTACT_FIELDS, DETAILS_FIELD],
    },
  ],
  submit: { de: 'Anfrage senden', en: 'Send inquiry' },
  confirmation: {
    title: { de: 'Anfrage angekommen', en: 'Inquiry received' },
    text: {
      de: 'CRE8NOW prüft vorab, welche Automatisierung sich bei eurem Volumen zuerst rechnet, und meldet sich innerhalb eines Werktags.',
      en: 'CRE8NOW works out which automation pays off first at your volume and gets back within one business day.',
    },
  },
};

/* --------------------------------------------------------------- CONCIERGE */

const CONCIERGE_TASKS = {
  doctor: [
    { id: 'appointments', de: 'Terminvereinbarung', en: 'Appointment booking' },
    { id: 'prescriptions', de: 'Rezeptbestellung', en: 'Prescription orders' },
    { id: 'patientsupport', de: 'Patienten-Support', en: 'Patient support' },
    { id: 'always', de: '24/7 Erreichbarkeit', en: '24/7 availability' },
    { id: 'results', de: 'Befundbesprechung', en: 'Test result callbacks' },
    { id: 'followups', de: 'Folgetermine', en: 'Follow-ups' },
    { id: 'sicknotes', de: 'Krankmeldungen', en: 'Sick note requests' },
  ],
  lawyer: [
    { id: 'consultation', de: 'Erstberatung-Termine', en: 'Consultation booking' },
    { id: 'onboarding', de: 'Mandanten-Onboarding', en: 'Client onboarding' },
    { id: 'callback', de: 'Rückruf-Service', en: 'Callback service' },
    { id: 'always', de: '24/7 Erreichbarkeit', en: '24/7 availability' },
    { id: 'casestatus', de: 'Verfahrensstand', en: 'Case status' },
    { id: 'fees', de: 'Gebühren-Auskunft', en: 'Fee information' },
  ],
  dealership: [
    { id: 'testdrive', de: 'Probefahrt-Termine', en: 'Test drive booking' },
    { id: 'tradein', de: 'Inzahlungnahme-Flow', en: 'Trade-in flow' },
    { id: 'service', de: 'Service-Termine', en: 'Service appointments' },
    { id: 'leadqual', de: 'Lead-Qualifizierung', en: 'Lead qualification' },
    { id: 'always', de: '24/7 Erreichbarkeit', en: '24/7 availability' },
    { id: 'financing', de: 'Finanzierung-Support', en: 'Financing support' },
  ],
  hotel: [
    { id: 'rooms', de: 'Zimmerbuchung', en: 'Room booking' },
    { id: 'concierge', de: 'Concierge-Service', en: 'Concierge service' },
    { id: 'checkin', de: 'Check-in-Support', en: 'Check-in support' },
    { id: 'always', de: '24/7 Erreichbarkeit', en: '24/7 availability' },
    { id: 'dining', de: 'Speisen & Getränke', en: 'Dining & drinks' },
    { id: 'upgrades', de: 'Zimmer-Upgrades', en: 'Room upgrades' },
  ],
  restaurant: [
    { id: 'reservation', de: 'Tischreservierung', en: 'Table reservation' },
    { id: 'hours', de: 'Öffnungszeiten-FAQ', en: 'Opening hours FAQ' },
    { id: 'orders', de: 'Bestellannahme', en: 'Order intake' },
    { id: 'always', de: '24/7 Erreichbarkeit', en: '24/7 availability' },
    { id: 'menu', de: 'Speisekarte-Anfragen', en: 'Menu inquiries' },
    { id: 'allergies', de: 'Allergie-Management', en: 'Allergy management' },
  ],
  fitness: [
    { id: 'trial', de: 'Probetraining-Buchung', en: 'Trial session booking' },
    { id: 'classes', de: 'Kurs-Infos & -Buchung', en: 'Class info & booking' },
    { id: 'membership', de: 'Mitgliedschaft-Fragen', en: 'Membership FAQ' },
    { id: 'always', de: '24/7 Erreichbarkeit', en: '24/7 availability' },
    { id: 'contracts', de: 'Verträge & Kündigung', en: 'Contracts & cancellations' },
  ],
  salon: [
    { id: 'booking', de: 'Terminbuchung', en: 'Appointment booking' },
    { id: 'cancellations', de: 'Stornierungs-Management', en: 'Cancellation management' },
    { id: 'products', de: 'Produktanfragen', en: 'Product inquiries' },
    { id: 'always', de: '24/7 Erreichbarkeit', en: '24/7 availability' },
    { id: 'treatments', de: 'Behandlungs-Infos', en: 'Treatment info' },
    { id: 'vouchers', de: 'Gutschein-Infos', en: 'Voucher info' },
  ],
  realestate: [
    { id: 'viewings', de: 'Besichtigungstermine', en: 'Viewing appointments' },
    { id: 'expose', de: 'Exposé-Versand', en: 'Exposé delivery' },
    { id: 'leadqual', de: 'Qualifizierung', en: 'Lead qualification' },
    { id: 'always', de: '24/7 Erreichbarkeit', en: '24/7 availability' },
    { id: 'financing', de: 'Finanzierungs-Infos', en: 'Financing info' },
  ],
  other: [
    { id: 'support', de: 'Support-Automatisierung', en: 'Support automation' },
    { id: 'booking', de: 'Terminbuchung', en: 'Appointment booking' },
    { id: 'leadqual', de: 'Lead-Qualifizierung', en: 'Lead qualification' },
    { id: 'always', de: '24/7 Erreichbarkeit', en: '24/7 availability' },
    { id: 'crm', de: 'CRM-Integration', en: 'CRM integration' },
  ],
};

const CONCIERGE_PAINS = {
  doctor: [
    { id: 'busyphone', de: 'Telefon morgens dauerbelegt', en: 'Phones busy every morning' },
    { id: 'prescriptions', de: 'Rezeptwünsche stören den Ablauf', en: 'Prescription requests disrupt the day' },
    { id: 'latecancel', de: 'Terminabsagen zu spät', en: 'Late cancellations' },
    { id: 'queue', de: 'Patienten in der Warteschleife', en: 'Patients stuck on hold' },
    { id: 'staff', de: 'Überlastetes Praxisteam', en: 'Overloaded front desk' },
  ],
  lawyer: [
    { id: 'prequal', de: 'Viel Vor-Qualifizierung nötig', en: 'Heavy pre-qualification needed' },
    { id: 'badtiming', de: 'Mandanten rufen zur Unzeit an', en: 'Clients call at bad times' },
    { id: 'status', de: 'Status-Updates kosten Zeit', en: 'Status updates cost time' },
    { id: 'bottleneck', de: 'Sekretariat ist Flaschenhals', en: 'Front desk is the bottleneck' },
    { id: 'onboarding', de: 'Manuelles Onboarding', en: 'Manual onboarding work' },
  ],
  dealership: [
    { id: 'coldleads', de: 'Probefahrt-Leads kühlen ab', en: 'Test drive leads cool off' },
    { id: 'workshop', de: 'Werkstatt-Termine binden das Team', en: 'Service booking ties up the team' },
    { id: 'tradein', de: 'Inzahlungnahme-Anfragen stauen sich', en: 'Trade-in requests pile up' },
    { id: 'callback', de: 'Lange Rückrufzeiten', en: 'Long callback wait times' },
    { id: 'qualeffort', de: 'Hoher Qualifizierungsaufwand', en: 'High qualification effort' },
  ],
  hotel: [
    { id: 'checkinpeak', de: 'Check-in-Peak überlastet', en: 'Check-in peak overload' },
    { id: 'repetitive', de: 'Immer gleiche Concierge-Fragen', en: 'Repetitive concierge questions' },
    { id: 'latearrival', de: 'Späte Anreisen ohne Support', en: 'Late arrivals without support' },
    { id: 'admin', de: 'Viel Admin für Mini-Infos', en: 'Heavy admin for simple info' },
    { id: 'dropoff', de: 'Buchungsabbrüche mangels Hilfe', en: 'Booking drop-offs' },
  ],
  restaurant: [
    { id: 'rushhour', de: 'Reservierungen während der Rush-Hour', en: 'Rush-hour reservations' },
    { id: 'hourcalls', de: 'Ständige Anrufe zu Öffnungszeiten', en: 'Constant opening hour calls' },
    { id: 'staff', de: 'Personalmangel am Telefon', en: 'Staff shortage for phones' },
    { id: 'ordererrors', de: 'Bestellfehler bei Telefonannahme', en: 'Order errors via phone' },
    { id: 'afterhours', de: 'Keine Erreichbarkeit nach 22 Uhr', en: 'No availability after 10 pm' },
  ],
  fitness: [
    { id: 'missedtrials', de: 'Verpasste Probetrainings', en: 'Missed trial sessions' },
    { id: 'infocalls', de: 'Viel Telefon-Support für Kurs-Infos', en: 'High phone support for class info' },
    { id: 'weekend', de: 'Leads gehen am Wochenende verloren', en: 'Leads lost on weekends' },
    { id: 'office', de: 'Büro-Überlastung', en: 'Office overload' },
    { id: 'followup', de: 'Inkonstante Lead-Nachfassung', en: 'Inconsistent lead follow-up' },
  ],
  salon: [
    { id: 'ringsduringcut', de: 'Telefon klingelt während der Behandlung', en: 'Phone rings mid-treatment' },
    { id: 'shortcancel', de: 'Kurzfristige Absagen', en: 'Short-notice cancellations' },
    { id: 'slotqueries', de: 'Dauernde Fragen nach freien Slots', en: 'Constant queries for open slots' },
    { id: 'afterhours', de: 'Kein Booking nach Feierabend', en: 'No booking after hours' },
    { id: 'admin', de: 'Viel Admin für ein kleines Team', en: 'Heavy admin for a small team' },
  ],
  realestate: [
    { id: 'emailflood', de: 'E-Mail-Flut bei neuen Objekten', en: 'Email flood for new listings' },
    { id: 'qualtime', de: 'Viel Zeit für Qualifizierung', en: 'Qualification eats time' },
    { id: 'repetitive', de: 'Immer gleiche Exposé-Anfragen', en: 'Repetitive listing inquiries' },
    { id: 'weekend', de: 'Erreichbarkeit am Wochenende', en: 'Weekend availability gap' },
    { id: 'viewingorga', de: 'Besichtigungs-Orga frisst Zeit', en: 'Viewing logistics eat time' },
  ],
  other: [
    { id: 'manual', de: 'Viel manuelle Routinearbeit', en: 'Heavy manual routine work' },
    { id: 'unreachable', de: 'Schlechte Erreichbarkeit', en: 'Poor availability' },
    { id: 'leadslost', de: 'Leads gehen verloren', en: 'Leads getting lost' },
    { id: 'staffcost', de: 'Hohe Personalkosten im Support', en: 'High support staff costs' },
    { id: 'slowreplies', de: 'Lange Antwortzeiten', en: 'Slow response times' },
  ],
};

const concierge = {
  type: 'concierge_demo',
  subject: { de: 'Concierge Live-Demo', en: 'Concierge Live Demo' },
  eyebrow: { de: 'Digital Concierge', en: 'Digital Concierge' },
  title: { de: 'Live-Demo buchen', en: 'Book a live demo' },
  subtitle: {
    de: 'Vier kurze Schritte. Je genauer die Angaben, desto näher ist die Demo an deinem echten Betrieb.',
    en: 'Four short steps. The more precise the answers, the closer the demo sits to your real operation.',
  },
  steps: [
    {
      label: { de: 'Branche', en: 'Industry' },
      fields: [{
        id: 'industry', role: 'industry', kind: 'single', icon: 'building', required: true,
        de: 'In welcher Branche bist du?', en: "What's your industry?",
        tooltip: {
          de: 'Steuert die folgenden Schritte — jede Branche hat andere Anrufgründe.',
          en: 'Drives the following steps — every industry gets called for different reasons.',
        },
        options: [
          { id: 'doctor', de: 'Arztpraxis', en: 'Medical practice' },
          { id: 'lawyer', de: 'Kanzlei', en: 'Law firm' },
          { id: 'dealership', de: 'Autohaus', en: 'Car dealership' },
          { id: 'hotel', de: 'Hotel', en: 'Hotel' },
          { id: 'restaurant', de: 'Restaurant', en: 'Restaurant' },
          { id: 'fitness', de: 'Fitness / Yoga', en: 'Fitness / yoga' },
          { id: 'salon', de: 'Friseur / Beauty', en: 'Salon / beauty' },
          { id: 'realestate', de: 'Makler', en: 'Real estate' },
          { id: 'other', de: 'Andere', en: 'Other' },
        ],
      }],
    },
    {
      label: { de: 'Aufgaben', en: 'Tasks' },
      fields: [{
        id: 'tasks', role: 'goals', kind: 'multi', icon: 'zap', required: true,
        de: 'Was soll der Concierge übernehmen?', en: 'What should the concierge handle?',
        tooltip: {
          de: 'Diese Aufgaben baut CRE8NOW in den Demo-Agenten ein.',
          en: 'These are the tasks CRE8NOW builds into the demo agent.',
        },
        optionsBy: { field: 'industry', map: CONCIERGE_TASKS, fallback: 'other' },
        emptyHint: { de: 'Bitte zuerst die Branche wählen.', en: 'Pick an industry first.' },
      }],
    },
    {
      label: { de: 'Engpass', en: 'Bottleneck' },
      fields: [
        {
          id: 'painpoints', kind: 'multi', icon: 'alert', required: true,
          de: 'Wo hakt es heute?', en: 'Where does it break down today?',
          tooltip: {
            de: 'Zeigt, welchen Anrufgrund der Agent zuerst abfangen soll.',
            en: 'Shows which call reason the agent should catch first.',
          },
          optionsBy: { field: 'industry', map: CONCIERGE_PAINS, fallback: 'other' },
          emptyHint: { de: 'Bitte zuerst die Branche wählen.', en: 'Pick an industry first.' },
        },
        {
          id: 'volume', mailLabel: 'CALLS PER MONTH', kind: 'single', icon: 'chart',
          de: 'Anrufe pro Monat (optional)', en: 'Calls per month (optional)',
          tooltip: {
            de: 'Bestimmt die Kostenrechnung, die CRE8NOW im Termin zeigt.',
            en: 'Drives the cost case CRE8NOW shows on the call.',
          },
          options: [
            { id: 'lt100', de: 'unter 100', en: 'under 100' },
            { id: '100-500', de: '100–500', en: '100–500' },
            { id: '500-2000', de: '500–2.000', en: '500–2,000' },
            { id: 'gt2000', de: '2.000+', en: '2,000+' },
          ],
        },
        {
          id: 'languages', mailLabel: 'LANGUAGES', kind: 'multi', icon: 'globe',
          de: 'In welchen Sprachen? (optional)', en: 'In which languages? (optional)',
          tooltip: {
            de: 'Der Agent spricht mehrsprachig — das legt fest, welche Stimmen vorbereitet werden.',
            en: 'The agent is multilingual — this sets which voices get prepared.',
          },
          options: [
            { id: 'de', de: 'Deutsch', en: 'German' },
            { id: 'en', de: 'Englisch', en: 'English' },
            { id: 'tr', de: 'Türkisch', en: 'Turkish' },
            { id: 'more', de: 'Weitere', en: 'Others' },
          ],
        },
      ],
    },
    {
      label: { de: 'Kontakt', en: 'Contact' },
      fields: [
        CONTACT_FIELDS[0],
        CONTACT_FIELDS[1],
        {
          id: 'phone', kind: 'tel', icon: 'phone', required: true,
          de: 'Telefonnummer', en: 'Phone number',
          placeholder: { de: '+49 …', en: '+49 …' },
        },
        CONTACT_FIELDS[2],
        CONTACT_FIELDS[3],
        {
          id: 'tools', mailLabel: 'PHONE / CALENDAR TOOLING', kind: 'text', icon: 'database',
          de: 'Telefonanlage / Kalender', en: 'Phone system / calendar',
          placeholder: { de: 'z. B. 3CX, Doctolib, Google Kalender', en: 'e.g. 3CX, Calendly, Google Calendar' },
        },
        DETAILS_FIELD,
      ],
      note: {
        de: 'Die Telefonnummer ist Pflicht — die Demo läuft über einen echten Anruf.',
        en: 'A phone number is required — the demo runs as a real call.',
      },
    },
  ],
  submit: { de: 'Live-Demo anfordern', en: 'Request live demo' },
  confirmation: {
    title: { de: 'Demo angefragt', en: 'Demo requested' },
    text: {
      de: 'CRE8NOW meldet sich innerhalb eines Werktags zur Terminabstimmung und bereitet die Demo auf deine Branche vor.',
      en: 'CRE8NOW gets back within one business day to arrange a time and prepares the demo for your industry.',
    },
  },
};

/* --------------------------------------------------------------- PROTOTYPE */

const prototyping = {
  type: 'prototype_scope',
  subject: { de: 'Scope-Call', en: 'Scope Call' },
  eyebrow: { de: 'Rapid Prototyping', en: 'Rapid Prototyping' },
  title: { de: 'Scope-Call buchen', en: 'Book a scope call' },
  subtitle: {
    de: 'Drei kurze Schritte. Danach kann CRE8NOW Festpreis und Lieferzeit grob einordnen.',
    en: 'Three short steps. Enough for CRE8NOW to place a fixed price and delivery time.',
  },
  steps: [
    {
      label: { de: 'Idee', en: 'Idea' },
      fields: [{
        id: 'outcome', role: 'goals', kind: 'multi', icon: 'zap', required: true,
        de: 'Was soll am Ende existieren?', en: 'What should exist at the end?',
        tooltip: {
          de: 'Der Zweck entscheidet über Tiefe und Machart des Prototyps.',
          en: 'The purpose decides how deep and how polished the prototype gets.',
        },
        options: [
          { id: 'proto', de: 'Klickbarer Produkt-Prototyp', en: 'Clickable product prototype' },
          { id: 'mvp', de: 'MVP für Nutzertests', en: 'MVP for user testing' },
          { id: 'investor', de: 'Investoren-Demo', en: 'Investor demo' },
          { id: 'poc', de: 'Proof of Concept für ein Feature', en: 'Proof of concept for one feature' },
          { id: 'sales', de: 'Pitch- / Sales-Demo', en: 'Pitch / sales demo' },
          { id: 'open', de: 'Noch offen', en: 'Not decided yet' },
        ],
      }],
    },
    {
      label: { de: 'Umfang', en: 'Scope' },
      fields: [
        {
          id: 'definition', mailLabel: 'IDEA MATURITY', kind: 'single', icon: 'sparkles', required: true,
          de: 'Wie weit ist die Idee?', en: 'How far along is the idea?',
          tooltip: {
            de: 'Gleiche Stufen wie im Konfigurator — das macht die Schätzung vergleichbar.',
            en: 'Same stages as the configurator, so the estimate stays comparable.',
          },
          options: [
            { id: 'defined', de: 'Bereits definiert', en: 'Already defined' },
            { id: 'ux-open', de: 'Idee klar, UX muss noch entstehen', en: 'Idea clear, UX needs work' },
            { id: 'early', de: 'Frühe Idee', en: 'Early idea' },
          ],
        },
        {
          id: 'scope', mailLabel: 'SCOPE SIZE', kind: 'single', icon: 'database', required: true,
          de: 'Wie groß ist der Umfang?', en: 'How large is the scope?',
          tooltip: {
            de: 'Der größte einzelne Hebel auf Preis und Lieferzeit.',
            en: 'The single biggest lever on price and delivery time.',
          },
          options: [
            { id: 'one-flow', de: 'Ein zentraler Flow', en: 'One key flow' },
            { id: 'one-feature', de: 'Ein vollständiges Feature', en: 'One complete feature' },
            { id: 'connected', de: 'Mehrere verbundene Bereiche', en: 'Several connected parts' },
            { id: 'broad', de: 'Breiteres Produktkonzept', en: 'Broader product concept' },
          ],
        },
        {
          id: 'timeline', kind: 'single', icon: 'calendar', required: true,
          de: 'Wann brauchst du es?', en: 'When do you need it?',
          tooltip: {
            de: 'Der Basis-Prototyp steht in sieben Werktagen, Erweiterungen kommen obendrauf.',
            en: 'The base prototype ships in seven business days, additions come on top.',
          },
          options: [
            { id: '2w', de: 'In 2 Wochen', en: 'In 2 weeks' },
            { id: '4w', de: 'In 4 Wochen', en: 'In 4 weeks' },
            { id: '68w', de: 'In 6–8 Wochen', en: 'In 6–8 weeks' },
            { id: 'open', de: 'Offen', en: 'Open' },
          ],
        },
        {
          id: 'budget', kind: 'single', icon: 'chart', required: true,
          de: 'Budgetrahmen', en: 'Budget range',
          tooltip: {
            de: 'Der Basis-Prototyp startet bei 2.500 €. Der Festpreis richtet sich nach dem Umfang.',
            en: 'The base prototype starts at €2,500. The fixed price follows the scope.',
          },
          options: [
            { id: 'lt5k', de: 'bis 5k €', en: 'up to €5k' },
            { id: '5-10k', de: '5–10k €', en: '€5–10k' },
            { id: '10-20k', de: '10–20k €', en: '€10–20k' },
            { id: 'gt20k', de: '20k+ €', en: '€20k+' },
            { id: 'openbudget', de: 'noch offen', en: 'still open' },
          ],
        },
      ],
    },
    {
      label: { de: 'Kontakt', en: 'Contact' },
      fields: [
        CONTACT_FIELDS[0],
        CONTACT_FIELDS[1],
        CONTACT_FIELDS[2],
        {
          id: 'website', kind: 'text', icon: 'globe',
          de: 'Website oder Deck-Link (optional)', en: 'Website or deck link (optional)',
          placeholder: { de: 'https://…', en: 'https://…' },
        },
        DETAILS_FIELD,
      ],
      configuratorHint: {
        de: 'Lieber selbst durchrechnen?',
        en: 'Rather price it yourself?',
        body: {
          de: 'Im Konfigurator stellst du deinen Scope selbst zusammen und siehst Festpreis und Lieferzeit sofort.',
          en: 'The configurator lets you assemble the scope yourself and shows fixed price and delivery time right away.',
        },
        link: { de: 'Konfigurator öffnen', en: 'Open the configurator' },
      },
    },
  ],
  submit: { de: 'Scope-Call anfragen', en: 'Request scope call' },
  confirmation: {
    title: { de: 'Scope-Call angefragt', en: 'Scope call requested' },
    text: {
      de: 'CRE8NOW schickt vor dem Termin eine erste Einschätzung zu Festpreis und Lieferzeit auf Basis deiner Angaben.',
      en: 'CRE8NOW sends a first estimate of fixed price and delivery time before the call, based on what you submitted.',
    },
  },
};

export const INQUIRY_PACKS = { design, ai, concierge, prototyping };

/* Shared chrome — identical across all four packs, so it lives once. */
export const SHELL_COPY = {
  de: {
    step: 'Schritt',
    back: 'Zurück',
    next: 'Weiter',
    close: 'Schließen',
    cancel: 'Abbrechen',
    sending: 'Wird gesendet…',
    required: 'Pflichtfeld',
    invalidEmail: 'E-Mail ungültig',
    error: 'Versand fehlgeschlagen. Schreib direkt an hello@cre8now.design.',
  },
  en: {
    step: 'Step',
    back: 'Back',
    next: 'Next',
    close: 'Close',
    cancel: 'Cancel',
    sending: 'Sending…',
    required: 'Required',
    invalidEmail: 'Invalid email',
    error: 'Submission failed. Write to hello@cre8now.design directly.',
  },
};

export const getPack = (area) => INQUIRY_PACKS[area] || INQUIRY_PACKS.design;
